import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { theme } from './theme'
import { api, ReconcileRequiredError } from './api'
import { isoDate, addDays, startOfWeekMon, SLOT_KEYS } from './dates'
import type { Me, Recipe, Meal, Plan, SlotKey, SlotMask, Rule } from './types'
import { subscribeEvents, type Patch } from './events'
import { Sidebar, BottomTabs, Wordmark, UserChipCompact, type TabId } from './components/Shell'
import { PlanTabDesktop, PlanTabMobile } from './components/PlanTab'
import { RecipesTab } from './components/RecipesTab'
import { PantryTab } from './components/PantryTab'
import { RecipePickerModal } from './components/RecipePickerModal'
import { LeftoverStashModal } from './components/LeftoverStashModal'
import { RecipeViewPage } from './components/RecipeViewPage'
import { RecipeEditPage } from './components/RecipeEditPage'
import { ChatBubble } from './components/ChatBubble'
import { PreferencesTab } from './components/PreferencesTab'
import { CookReconcileModal } from './components/CookReconcileModal'
import { AlertDialog } from './components/Dialog'
import { AgentDocViewer } from './components/AgentDocViewer'
import { GrantAcceptPage } from './components/AccountSharing'
import type { ReconcileRequired, ReconcilePayload } from './types'
import { usePath, useNavigate } from './router'
import { matchRoute } from './routes'
import type { MealStatus } from './types'

function useViewport() {
  const [mobile, setMobile] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches)
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)')
    const handler = (e: MediaQueryListEvent) => setMobile(e.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])
  return mobile
}

// The chat FAB is position:fixed in the lower-right (56px tall, anchored
// bottom:24 desktop / bottom:78 mobile — see ChatBubble). Without slack at the
// bottom of the scroll container, a page's lowest interactive element (e.g. the
// delete icon on the last custom tag in recipe edit) sits permanently under the
// bubble and can't be tapped. Padding the one shared scroll container lets every
// page scroll far enough to lift that element clear of the FAB — generically,
// with no per-page edits. Sized to FAB anchor + FAB height + a small gap.
const CHAT_FAB_CLEARANCE = 96

const TAB_PATHS: TabId[] = ['plan', 'recipes', 'pantry', 'preferences']

function tabFromPath(path: string): TabId | null {
  const seg = path.split('/').filter(Boolean)[0]
  return (TAB_PATHS as string[]).includes(seg) ? (seg as TabId) : null
}

export default function App() {
  const [me, setMe] = useState<Me | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetch('/api/me', { credentials: 'same-origin' })
      .then(r => (r.ok ? r.json() : null))
      .then(u => setMe(u))
      .finally(() => setLoading(false))
  }, [])

  if (loading) return null
  if (!me) return <LoginScreen/>
  return <Authed me={me} onMeChange={setMe} onLogout={() => setMe(null)}/>
}

function Authed({ me, onMeChange, onLogout }: { me: Me; onMeChange: (m: Me) => void; onLogout: () => void }) {
  const mobile = useViewport()
  const path = usePath()
  const navigate = useNavigate()
  const today = useMemo(() => { const d = new Date(); d.setHours(0,0,0,0); return d }, [])

  // Normalize unknown paths to /plan. The route catalog lives in routes.ts.
  useEffect(() => {
    if (!matchRoute(path)) navigate('/plan', { replace: true })
  }, [path, navigate])

  const tab: TabId = tabFromPath(path) ?? 'plan'
  const editId = (() => {
    const m = path.match(/^\/recipes\/(\d+)\/edit$/)
    return m ? Number(m[1]) : null
  })()
  const viewId = (() => {
    const m = path.match(/^\/recipes\/(\d+)$/)
    return m ? Number(m[1]) : null
  })()
  const mealViewId = (() => {
    const m = path.match(/^\/meals\/(\d+)$/)
    return m ? Number(m[1]) : null
  })()
  const setTab = useCallback((t: TabId) => navigate('/' + t), [navigate])

  const [recipes, setRecipes] = useState<Recipe[]>([])
  const [meals, setMeals] = useState<Meal[]>([])
  const [slotMask, setSlotMask] = useState<SlotMask | null>(null)
  const [rules, setRules] = useState<Rule[]>([])
  const [picker, setPicker] = useState<{ date: string; slot: SlotKey } | null>(null)
  const [reconcile, setReconcile] = useState<{ mealID: number; env: ReconcileRequired; leftoverServings?: number } | null>(null)
  // "Save leftovers?" prompt shown before cooking a batch recipe.
  const [stash, setStash] = useState<{ mealID: number; recipeName: string; maxServings: number } | null>(null)
  const [alertMsg, setAlertMsg] = useState<string | null>(null)

  // Plan window: load a generous range around today.
  const loadFrom = useMemo(() => isoDate(addDays(startOfWeekMon(today), -28)), [today])
  const loadTo   = useMemo(() => isoDate(addDays(startOfWeekMon(today), 84)),  [today])

  const refresh = useCallback(async () => {
    const [rs, ms, sm, rl] = await Promise.all([
      api.recipes(), api.meals(loadFrom, loadTo), api.slotMask(), api.rules(),
    ])
    setRecipes(rs)
    setMeals(ms)
    setSlotMask(sm)
    setRules(rl)
  }, [loadFrom, loadTo])

  useEffect(() => { refresh().catch(e => console.error(e)) }, [refresh])

  // Keep the latest `me` accessible inside the long-lived SSE handler below.
  const meRef = useRef(me)
  useEffect(() => { meRef.current = me }, [me])

  // Live patch stream: server pushes {op, table, row|id} for every write to
  // user-owned data, including writes from other tabs or agent tool calls.
  useEffect(() => {
    const ctrl = new AbortController()
    const onPatch = (p: Patch) => {
      if (p.table === 'meal') {
        if (p.op === 'upsert') {
          setMeals(ms => {
            const i = ms.findIndex(m => m.id === p.row.id)
            if (i < 0) return [...ms, p.row]
            const next = ms.slice(); next[i] = p.row; return next
          })
        } else {
          setMeals(ms => ms.filter(m => m.id !== p.id))
        }
      } else if (p.table === 'recipe') {
        if (p.op === 'upsert') {
          setRecipes(rs => {
            const i = rs.findIndex(r => r.id === p.row.id)
            if (i < 0) return [...rs, p.row].sort((a, b) => a.name.localeCompare(b.name))
            const next = rs.slice(); next[i] = p.row; return next
          })
        } else {
          setRecipes(rs => rs.filter(r => r.id !== p.id))
        }
      } else if (p.table === 'credits') {
        if (p.op === 'upsert') onMeChange({ ...meRef.current, credits: p.row.balance })
      } else if (p.table === 'slot_mask') {
        if (p.op === 'upsert') setSlotMask(p.row)
      } else if (p.table === 'rule') {
        if (p.op === 'upsert') {
          setRules(rs => {
            const i = rs.findIndex(r => r.id === p.row.id)
            if (i < 0) return [...rs, p.row]
            const next = rs.slice(); next[i] = p.row; return next
          })
        } else {
          setRules(rs => rs.filter(r => r.id !== p.id))
        }
      } else if (p.table === 'ingredient' || p.table === 'pantry_item' || p.table === 'shopping_item') {
        // PantryTab keeps its own state; broadcast for it to react.
        window.dispatchEvent(new CustomEvent('mila:patch', { detail: p }))
      }
    }
    subscribeEvents(onPatch, ctrl.signal).catch(() => {})
    return () => ctrl.abort()
  }, [])

  // Recover missed events when the tab regains focus.
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === 'visible') {
        refresh().catch(e => console.error(e))
      }
    }
    document.addEventListener('visibilitychange', onVis)
    return () => document.removeEventListener('visibilitychange', onVis)
  }, [refresh])

  const recipesById = useMemo(() => {
    const m = new Map<number, Recipe>()
    for (const r of recipes) m.set(r.id, r)
    return m
  }, [recipes])

  // Both recipe and meal detail are now full-screen pages driven by the URL:
  // /recipes/{id} (read-only recipe) and /meals/{id} (a planned meal in the
  // context of its slot). The page itself fetches the heavy detail; here we
  // resolve the lightweight summary/meal from already-loaded state.
  const viewRecipe = viewId != null ? recipesById.get(viewId) ?? null : null
  const viewMeal = mealViewId != null ? meals.find(m => m.id === mealViewId) ?? null : null
  const mealRecipe = viewMeal?.recipe_id != null ? recipesById.get(viewMeal.recipe_id) ?? null : null

  // If the URL points at a recipe/meal that no longer exists (deleted recipe,
  // cleared slot, or a stale link), fall back to its parent list. Guarded on
  // "data has loaded" so the initial empty state doesn't bounce a valid link.
  useEffect(() => {
    if (viewId != null && recipes.length > 0 && !recipesById.has(viewId))
      navigate('/recipes', { replace: true })
  }, [viewId, recipesById, recipes.length, navigate])
  useEffect(() => {
    if (mealViewId != null && meals.length > 0 &&
        !meals.some(m => m.id === mealViewId && m.recipe_id != null))
      navigate('/plan', { replace: true })
  }, [mealViewId, meals, navigate])

  const plan: Plan = useMemo(() => {
    const p: Plan = {}
    for (const m of meals) {
      if (!p[m.date]) p[m.date] = {}
      p[m.date]![m.slot] = m
    }
    return p
  }, [meals])

  const plannedThisWeek = useMemo(() => {
    const ws = startOfWeekMon(today)
    let n = 0
    for (let i = 0; i < 7; i++) {
      const dp = plan[isoDate(addDays(ws, i))]
      if (!dp) continue
      for (const s of SLOT_KEYS) if (dp[s]) n++
    }
    return n
  }, [plan, today])

  const onSlotClick = (date: string, slot: SlotKey) => {
    const m = plan[date]?.[slot]
    // A filled slot pushes the meal page so the user can navigate back to the
    // plan; an empty slot opens the picker as before.
    if (m && m.recipe_id != null) { navigate(`/meals/${m.id}`); return }
    setPicker({ date, slot })
  }

  const onRecipeOpen = (recipe: Recipe) => navigate(`/recipes/${recipe.id}`)

  // Create a blank draft recipe and drop straight into its editor. The SSE
  // upsert merges the new row into `recipes`, so the editor's fetch-by-id finds
  // it immediately.
  const onRecipeNew = async () => {
    try {
      const created = await api.createRecipe()
      navigate(`/recipes/${created.id}/edit`)
    } catch (e) {
      setAlertMsg(String(e))
    }
  }

  const onMealRemove = async () => {
    if (!viewMeal) return
    try {
      await api.setSlot(viewMeal.date, viewMeal.slot, null)
      navigate('/plan')
    } catch (e) { console.error(e) }
  }

  const onSlotClear = async (date: string, slot: SlotKey) => {
    try { await api.setSlot(date, slot, null) }
    catch (e) { console.error(e) }
  }

  // "Change" stays on the meal page and opens the picker as an overlay; the
  // slot upsert keeps the same meal id, so the page just re-renders with the
  // newly chosen recipe once the live patch lands.
  const onMealChange = () => {
    if (!viewMeal) return
    setPicker({ date: viewMeal.date, slot: viewMeal.slot })
  }

  // doCook marks a meal cooked, optionally stashing `leftoverServings` of the
  // dish as a recipe lot. Routes the pantry-reconcile 409 into the modal,
  // remembering the leftover count so the forced re-call still stashes.
  const doCook = async (mealID: number, leftoverServings?: number) => {
    try {
      await api.setStatus(mealID, 'cooked',
        leftoverServings ? { leftover_servings: leftoverServings } : undefined)
    } catch (e) {
      if (e instanceof ReconcileRequiredError) {
        setReconcile({ mealID, env: e.envelope, leftoverServings })
        return
      }
      console.error(e)
    }
  }

  const onMealStatus = async (status: MealStatus) => {
    if (!viewMeal) return
    const mealID = viewMeal.id
    // Cooking a batch recipe (servings > 1) that isn't itself a leftover: offer
    // to stash the surplus as an on-hand recipe lot before cooking.
    if (status === 'cooked') {
      const rec = recipes.find(r => r.id === viewMeal.recipe_id)
      const servings = rec?.servings ?? 0
      if (!viewMeal.from_pantry_item_id && servings > 1) {
        setStash({ mealID, recipeName: rec!.name, maxServings: servings })
        return
      }
      await doCook(mealID)
      return
    }
    try {
      // The status write publishes a meal patch; `viewMeal` re-resolves from
      // the updated `meals` state, so no local optimistic update is needed.
      await api.setStatus(mealID, status)
    } catch (e) {
      console.error(e)
    }
  }

  const onReconcileConfirm = async (payload: ReconcilePayload) => {
    if (!reconcile) return
    await api.setStatus(reconcile.mealID, 'cooked', {
      force: true, reconcile: payload, leftover_servings: reconcile.leftoverServings,
    })
    setReconcile(null)
  }

  const onDetailAdopt = async () => {
    if (viewId == null) return
    try {
      await api.adopt(viewId)
      navigate('/recipes')
    } catch (e) { console.error(e) }
  }

  // View-original / cross-recipe navigation just pushes the target recipe's
  // page; browser back returns to the page they came from.
  const onViewOriginal = (originalId: number) => navigate(`/recipes/${originalId}`)

  const onDetailDelete = async () => {
    if (viewId == null) return
    try {
      await api.deleteRecipe(viewId)
      navigate('/recipes')
    } catch (e) {
      console.error(e)
      setAlertMsg('Failed to delete recipe')
    }
  }

  const onPick = async (recipe: Recipe) => {
    if (!picker) return
    try {
      // The picker auto-adopts catalog rows before calling onPick, so by the
      // time we get here `recipe.id` always refers to a row in the user's
      // cookbook.
      await api.setSlot(picker.date, picker.slot, recipe.id, 'planned')
      setPicker(null)
    } catch (e) {
      console.error(e)
      setAlertMsg('Failed to update plan')
    }
  }

  const onPickLeftover = async (pantryItemId: number) => {
    if (!picker) return
    try {
      await api.setSlotLeftover(picker.date, picker.slot, pantryItemId)
      setPicker(null)
    } catch (e) {
      console.error(e)
      setAlertMsg('Failed to schedule leftover')
    }
  }

  const onClear = async () => {
    if (!picker) return
    try {
      await api.setSlot(picker.date, picker.slot, null)
      setPicker(null)
    } catch (e) {
      console.error(e)
    }
  }

  const onAdopt = async (recipe: Recipe) => {
    try { await api.adopt(recipe.id) }
    catch (e) { console.error(e) }
  }

  const handleLogout = async () => {
    await fetch('/api/logout', { method: 'POST', credentials: 'same-origin' })
    onLogout()
  }

  const currentMeal = picker ? plan[picker.date]?.[picker.slot] : undefined

  const uiScale = me.ui_scale || 1.0
  const onAgentDocs = path === '/agent-docs' || path.startsWith('/agent-docs/')

  const grantToken = (() => {
    const m = path.match(/^\/grant\/([A-Za-z0-9_-]+)$/)
    return m ? m[1] : null
  })()
  if (grantToken) {
    return <GrantAcceptPage token={grantToken} />
  }

  if (onAgentDocs) {
    return (
      <div style={{
        width: '100vw', height: '100dvh', background: theme.bg,
        color: theme.ink, fontFamily: theme.sans, overflowY: 'auto',
        paddingBottom: CHAT_FAB_CLEARANCE,
        zoom: uiScale, position: 'relative',
      } as React.CSSProperties}>
        <AgentDocViewer path={path.replace(/^\/agent-docs\/?/, '')}/>
        <ChatBubble mobile={mobile}/>
      </div>
    )
  }

  return (
    <div style={{
      width: '100vw', height: '100dvh', background: theme.bg,
      display: 'flex', flexDirection: mobile ? 'column' : 'row',
      color: theme.ink, fontFamily: theme.sans,
      position: 'relative', overflow: 'hidden',
      zoom: uiScale,
    } as React.CSSProperties}>
      {!mobile && <Sidebar tab={tab} setTab={setTab} me={me} plannedThisWeek={plannedThisWeek} onLogout={handleLogout}/>}

      {mobile && (
        <header style={{
          padding: '16px 18px 8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <Wordmark size={30}/>
          <UserChipCompact me={me} onLogout={handleLogout}/>
        </header>
      )}

      <main style={{
        flex: 1, minWidth: 0, minHeight: 0, overflowY: 'auto',
        padding: mobile
          ? `8px 18px ${CHAT_FAB_CLEARANCE}px`
          : `26px 36px ${CHAT_FAB_CLEARANCE}px`,
      }}>
        {editId !== null ? (
          <RecipeEditPage
            recipeId={editId}
            mobile={mobile}
            onDone={() => { navigate('/recipes') }}
            onCancel={() => navigate('/recipes')}
          />
        ) : viewId !== null ? (
          <RecipeViewPage
            recipeId={viewId}
            recipe={viewRecipe}
            mobile={mobile}
            onBack={() => navigate('/recipes')}
            onAdopt={onDetailAdopt}
            onEdit={() => navigate(`/recipes/${viewId}/edit`)}
            onDelete={onDetailDelete}
            onViewOriginal={onViewOriginal}
            alreadyAdopted={recipes.some(r => r.forked_from === viewId)}
          />
        ) : mealViewId !== null ? (
          viewMeal && viewMeal.recipe_id != null ? (
            <RecipeViewPage
              recipeId={viewMeal.recipe_id}
              recipe={mealRecipe}
              meal={viewMeal}
              mobile={mobile}
              onBack={() => navigate('/plan')}
              onChange={onMealChange}
              onRemove={onMealRemove}
              onSetStatus={onMealStatus}
              onEdit={() => navigate(`/recipes/${viewMeal.recipe_id}/edit`)}
              onViewOriginal={onViewOriginal}
            />
          ) : null
        ) : path === '/usage' ? (
          <UsagePage me={me} onMeChange={onMeChange}/>
        ) : (
          <>
            {tab === 'plan' && (mobile
              ? <PlanTabMobile today={today} plan={plan} recipesById={recipesById} onSlotClick={onSlotClick} onSlotClear={onSlotClear}/>
              : <PlanTabDesktop today={today} plan={plan} recipesById={recipesById} onSlotClick={onSlotClick} onSlotClear={onSlotClear}/>
            )}
            {tab === 'recipes' && <RecipesTab recipes={recipes} mobile={mobile} onAdopt={onAdopt} onOpen={onRecipeOpen} onNew={onRecipeNew}/>}
            {tab === 'pantry'  && <PantryTab mobile={mobile} me={me}/>}
            {tab === 'preferences' && <PreferencesTab me={me} onMeChange={onMeChange} slotMask={slotMask} onSlotMaskChange={setSlotMask} rules={rules} mobile={mobile}/>}
          </>
        )}
      </main>

      {mobile && <BottomTabs tab={tab} setTab={setTab}/>}

      <ChatBubble mobile={mobile}/>

      <RecipePickerModal
        open={picker !== null}
        onClose={() => setPicker(null)}
        dayIso={picker?.date ?? null}
        slotKey={picker?.slot ?? null}
        currentRecipeId={currentMeal?.recipe_id}
        onPick={onPick}
        onPickLeftover={onPickLeftover}
        onClear={onClear}
        onPreview={r => { setPicker(null); navigate(`/recipes/${r.id}`) }}
      />

      <AlertDialog
        open={alertMsg !== null}
        message={alertMsg ?? ''}
        onClose={() => setAlertMsg(null)}
      />

      <CookReconcileModal
        open={reconcile !== null}
        envelope={reconcile?.env ?? null}
        me={me}
        onCancel={() => setReconcile(null)}
        onConfirm={onReconcileConfirm}
      />

      <LeftoverStashModal
        open={stash !== null}
        recipeName={stash?.recipeName ?? ''}
        maxServings={stash?.maxServings ?? 0}
        onCancel={() => setStash(null)}
        onCook={(n) => { const s = stash; setStash(null); if (s) doCook(s.mealID, n) }}
      />
    </div>
  )
}

function UsagePage({ me, onMeChange }: { me: Me; onMeChange: (m: Me) => void }) {
  const [buyOpen, setBuyOpen] = useState(false)
  useEffect(() => {
    fetch('/api/credits', { credentials: 'same-origin' })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d && typeof d.balance === 'number') {
          onMeChange({ ...me, credits: d.balance })
        }
      })
      .catch(() => {})
    // intentional: only refetch on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const balance = me.credits ?? 0
  const dollars = (balance / 100).toFixed(2)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18, maxWidth: 640 }}>
      <div>
        <div style={{
          fontFamily: theme.sans, fontSize: 10, letterSpacing: 1.4, fontWeight: 600,
          textTransform: 'uppercase', color: theme.muted,
        }}>Mila</div>
        <h1 style={{
          fontFamily: theme.serif, fontSize: 42, fontWeight: theme.headWeight,
          color: theme.ink, margin: '2px 0 0', lineHeight: 1.05,
        }}>Usage</h1>
      </div>

      <section style={{
        background: theme.surface, border: theme.cardBorder,
        borderRadius: theme.radiusLg, padding: 24,
      }}>
        <div style={{
          fontFamily: theme.sans, fontSize: 11, letterSpacing: 1.4, fontWeight: 600,
          textTransform: 'uppercase', color: theme.muted,
        }}>Credit balance</div>
        <div style={{
          fontFamily: theme.serif, fontWeight: theme.headWeight,
          fontSize: 48, color: theme.ink, marginTop: 6, lineHeight: 1.05,
        }}>
          {balance.toFixed(1)}
          <span style={{ fontSize: 16, color: theme.muted, marginLeft: 10 }}>credits</span>
        </div>
        <div style={{ fontSize: 13, color: theme.inkSoft, marginTop: 6 }}>
          ≈ ${dollars} of chat usage at current rates.
          One credit ≈ $0.01 of model use.
        </div>

        <div style={{ marginTop: 20, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <button
            onClick={() => setBuyOpen(true)}
            style={{
              all: 'unset', cursor: 'pointer',
              padding: '10px 18px', borderRadius: theme.radius,
              background: theme.ink, color: theme.surface,
              fontFamily: theme.sans, fontSize: 14, fontWeight: 600,
            }}
          >
            Buy credits
          </button>
          {balance <= 0 && (
            <span style={{ fontSize: 13, color: theme.accent }}>
              Out of credits — chat will be paused until you top up.
            </span>
          )}
        </div>
      </section>

      <section style={{
        background: theme.surface, border: theme.cardBorder,
        borderRadius: theme.radiusLg, padding: 20, fontSize: 13, color: theme.inkSoft,
      }}>
        Credits are spent whenever you chat with Mila — each turn deducts a
        small amount based on the model's reported cost. New accounts start
        with 200 credits ($2.00) of usage.
      </section>

      <AlertDialog
        open={buyOpen}
        title="Buy credits"
        message="Self-serve top-ups aren't available yet. Email Josiah at fencepost13302@gmail.com and he'll add credits to your account."
        onClose={() => setBuyOpen(false)}
      />
    </div>
  )
}

function LoginScreen() {
  return (
    <div style={{
      width: '100vw', height: '100vh', background: theme.bg,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
      fontFamily: theme.sans, color: theme.ink,
    }}>
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 24,
        maxWidth: 360, textAlign: 'center',
      }}>
        <Wordmark size={140}/>
        <div style={{
          fontFamily: theme.serif, fontSize: 22, fontWeight: theme.headWeight,
          color: theme.inkSoft, lineHeight: 1.3,
        }}>
          A quiet companion for what's in your fridge and what's for dinner.
        </div>
        <a href="/auth/login" style={{ textDecoration: 'none' }}>
          <button style={{
            all: 'unset', cursor: 'pointer',
            padding: '12px 24px', borderRadius: theme.radius,
            background: theme.ink, color: theme.surface,
            fontFamily: theme.sans, fontSize: 14, fontWeight: 600, letterSpacing: 0.4,
          }}>
            Sign in with Google
          </button>
        </a>
      </div>
    </div>
  )
}
